import { useRef, useState } from "react";
import { LayoutChangeEvent, LayoutRectangle, StyleProp, StyleSheet, View, ViewStyle } from "react-native";
import Animated, { useAnimatedStyle, useSharedValue, withTiming } from "react-native-reanimated";
import { Button, ButtonProps } from "./Button";
import { useTheme } from "@/hooks/useTheme";
import { HEADER } from "@/components/Header/constants";

export type ToggleProps = {
  style?: StyleProp<ViewStyle>;
  textStyle?: StyleProp<import("react-native").TextStyle>;
  choices: string[] & ({ length: 2 } | { length: 3 } | { length: 4 });
  value?: string;
  onPick?: (item?: string) => void;
  buttonProps?: ButtonProps;
};

export function Toggle({ style, textStyle, choices, value: innerValue, onPick, buttonProps }: ToggleProps) {
  const { colors } = useTheme();
  const [value, setValue] = useState(innerValue ?? choices[0]);
  const layoutsRef = useRef<Record<number, LayoutRectangle>>({});
  const sel = useSharedValue<LayoutRectangle | null>(null);

  const onItemLayout = (i: number) => (e: LayoutChangeEvent) => {
    layoutsRef.current[i] = e.nativeEvent.layout;
    // Set initial position based on the current value, not just the first item
    if (choices[i] === value && !sel.get()) {
      sel.set(e.nativeEvent.layout);
    }
  };
  const onPressItem = (c: string, i: number) => () => {
    const l = layoutsRef.current[i];
    if (l) sel.set(l);
    onPick?.(c);
    setValue(c);
  };

  const indicatorStyle = useAnimatedStyle(() => {
    const l = sel.get();
    return {
      position: "absolute",
      borderRadius: HEADER.RADIUS,
      backgroundColor: colors.primary,
      borderWidth: 0,
      width: withTiming(l?.width ?? 0, { duration: 50 }),
      top: 0,
      bottom: 0,
      transform: [
        { translateX: withTiming((l?.x ?? 0) - 1, { duration: 100 }) },
        { translateY: withTiming((l?.y ?? 0) - 1, { duration: 100 }) },
      ],
    };
  });

  return (
    <View style={[styles.container, { borderColor: colors.border }, style]}>
      <Animated.View pointerEvents="none" style={[indicatorStyle]} />
      {choices.map((c, i) => (
        <Button
          key={i}
          text={c}
          style={[styles.button]}
          textStyle={textStyle}
          {...(value === c && { hoverColor: colors.primary })}
          onLayout={onItemLayout(i)}
          onPress={onPressItem(c, i)}
          {...buttonProps}
        />
      ))}
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    position: "relative",
    flexDirection: "row",
    borderWidth: StyleSheet.hairlineWidth,
    borderRadius: HEADER.RADIUS,
    overflow: "hidden",
    padding: 0,
    height: HEADER.HEIGHT,
  },
  button: {
    flex: 1,
    backgroundColor: "transparent",
    borderWidth: 0,
  },
});
